import { DEFAULT_FOUR_BALL_PAX, DEFAULT_PAX } from "@quote/consts";
import { RoomRule } from "@quote/quote/api/init_quote/types";
import pluralize from "pluralize";
import { type StateCreator } from "zustand";

import { isEmpty } from "@/utils/helpers";

import { splitNumber } from "../utils";
import {
  MIN_PAX_PER_ROOM,
  MAX_TOTAL_PAX,
  DEFAULT_MAX_BOOKING_PAX,
  DEFAULT_MAX_PAX_PER_ROOM,
  STANDARD_ROOM_NAME,
  STANDARD_ROOM_RULE,
} from "./constants";
import { RoomType, type RoomsState } from "./types";
import type { Room } from "./types";

const canIncrementPax = (pax: number, max: number) => pax + 1 <= max;

const canDecrementPax = (pax: number, min: number) => pax - 1 >= min;

const isPaxValidForLargeRoom = (pax: number, roomRules: RoomRule[]) =>
  roomRules.some(
    ({ minOccupancy, maxOccupancy }) =>
      pax >= minOccupancy && pax <= maxOccupancy,
  );

function bestRoom(remaining: number, rooms: RoomRule[]): RoomRule | undefined {
  const eligible = rooms.filter((r) => remaining >= r.minOccupancy);
  if (!eligible.length) return undefined;

  // Tightest fit (smallest maxOccupancy that still covers remaining)
  const fits = eligible.filter((r) => r.maxOccupancy >= remaining);
  if (fits.length > 0) {
    return fits.reduce((a, b) => (a.maxOccupancy <= b.maxOccupancy ? a : b));
  }

  // No room fits — pick largest to drain pax fastest
  return eligible.reduce((a, b) => (a.maxOccupancy >= b.maxOccupancy ? a : b));
}

function allocateRooms(pax: number, roomRules: RoomRule[]) {
  const allocations = [];
  let remaining = pax;

  while (remaining > 0) {
    const room = bestRoom(remaining, roomRules);
    const pax = room ? Math.min(remaining, room.maxOccupancy) : remaining;
    const isValid =
      pax <= DEFAULT_MAX_PAX_PER_ROOM || isPaxValidForLargeRoom(pax, roomRules);

    const roomData = {
      key: crypto.randomUUID(),
      pax,
      type: room ? RoomType.LargeRoom : RoomType.Standard,
      name: room ? room.typeName : STANDARD_ROOM_NAME,
      isValid: room ? isValid : true,
    };

    allocations.push(roomData);

    remaining -= pax;

    if (!room) break;
  }

  return allocations;
}

const addOrRemovePax = (
  addOrRemove: number,
  index: number,
  rooms: Room[],
  roomRules: RoomRule[],
): Room[] => {
  const pax = rooms[index].pax + addOrRemove;
  const isValid =
    pax <= DEFAULT_MAX_PAX_PER_ROOM || isPaxValidForLargeRoom(pax, roomRules);
  const suitableRoom = isValid ? bestRoom(pax, roomRules) : undefined;

  return rooms.map((room, i) =>
    i === index
      ? {
          ...room,
          pax,
          type: suitableRoom ? RoomType.LargeRoom : RoomType.Standard,
          name: suitableRoom?.typeName ?? STANDARD_ROOM_NAME,
          isValid,
        }
      : room,
  );
};

const getTotalRoomPax = (rooms: Room[]) =>
  rooms.reduce((acc, curr) => acc + curr.pax, 0);

const getFourBallPax = (pax: number) =>
  pax === DEFAULT_FOUR_BALL_PAX * 2
    ? DEFAULT_FOUR_BALL_PAX * 2
    : DEFAULT_FOUR_BALL_PAX;

const newError = (error: string) => new Error(`ROOMS.${error}`);

const initState = {
  prevState: {
    rooms: [],
  },
  rooms: [],
  roomRules: [],
  maxBookingPax: DEFAULT_MAX_BOOKING_PAX,
  maxRoomPax: DEFAULT_MAX_PAX_PER_ROOM,
};

export const createRoomsState: StateCreator<
  RoomsState,
  [["zustand/devtools", never]]
> = (set, get) => ({
  ...initState,

  roomsActions: {
    addPaxAt: (index: number) => {
      const { rooms, roomRules, maxRoomPax } = get();
      if (canIncrementPax(rooms[index].pax, maxRoomPax)) {
        const newRooms = addOrRemovePax(1, index, rooms, roomRules);
        set({ rooms: newRooms }, undefined, "room/addPaxAt");
      }
    },

    addRoom: (isFourBall: boolean) => {
      const { roomsUtils, roomRules } = get();
      const totalPax = roomsUtils.totalPax();

      if (totalPax < MAX_TOTAL_PAX) {
        const pax = isFourBall ? getFourBallPax(totalPax) : 1;

        set(
          {
            rooms: [...get().rooms, ...allocateRooms(pax, roomRules)],
          },
          undefined,
          "room/addRoom",
        );
      } else {
        throw newError("MAX_ROOMS_REACHED");
      }
    },

    removePaxAt: (index: number) => {
      const { rooms, roomRules } = get();
      if (canDecrementPax(rooms[index].pax, MIN_PAX_PER_ROOM)) {
        const newRooms = addOrRemovePax(-1, index, rooms, roomRules);
        set({ rooms: newRooms }, undefined, "room/removePaxAt");
      }
    },

    removeRoom: (index: number) => {
      const { roomsUtils, rooms } = get();

      if (roomsUtils.totalRooms() > 1) {
        set(
          { rooms: rooms.filter((_, i) => i !== index) },
          undefined,
          "room/removeRoom",
        );
      } else {
        throw newError("MIN_ROOMS_REACHED");
      }
    },

    setPrevState: () => {
      const { rooms } = get();
      set({ prevState: { rooms } }, undefined, "room/setPrevState");
    },

    resetToPrev: () => {
      const { rooms } = get().prevState ?? {
        rooms: [],
      };

      set({ prevState: null, rooms }, undefined, "room/resetToPrev");
    },

    resetState: () => set(initState, undefined, "room/resetState"),

    setRoomsFromPax: (groupSize, roomRules) => {
      const { roomRules: largeRoomsState } = get();
      const rooms = allocateRooms(groupSize, roomRules ?? largeRoomsState);

      set({ rooms }, undefined, "room/setRoomsFromPax");
    },

    setStateFormOffer: (offer, groupSize) => {
      const { setRoomsFromPax } = get().roomsActions;
      const { maxBookingPax, fourBall, accommodation } = offer;
      const { rooms } = accommodation[0] ?? {};
      const sortedRooms = isEmpty(rooms)
        ? STANDARD_ROOM_RULE
        : rooms.sort((a, b) => b.maxOccupancy - a.maxOccupancy);
      const maxRoomPax = isEmpty(sortedRooms)
        ? DEFAULT_MAX_PAX_PER_ROOM
        : Math.max(...sortedRooms.map(({ maxOccupancy }) => maxOccupancy));
      const pax = groupSize || (fourBall ? DEFAULT_FOUR_BALL_PAX : DEFAULT_PAX);

      setRoomsFromPax(pax, sortedRooms);
      set(
        {
          roomRules: sortedRooms,
          maxBookingPax,
          maxRoomPax,
        },
        undefined,
        "room/setStateFormOffer",
      );
    },
  },

  roomsUtils: {
    guestsCount: () =>
      get().rooms.reduce((acc, curr) => {
        if (curr.isValid) {
          return [...acc, curr.pax];
        }

        return [...acc, ...splitNumber(curr.pax, DEFAULT_MAX_PAX_PER_ROOM)];
      }, [] as number[]),

    isMaxPaxAt: (room) => room.pax >= get().maxRoomPax,

    isMaxRooms: () => get().roomsUtils.totalPax() >= MAX_TOTAL_PAX,

    isMinPaxAt: (room) => room.pax <= MIN_PAX_PER_ROOM,

    isMinRooms: () => get().roomsUtils.totalRooms() <= 1,

    totalPax: () => getTotalRoomPax(get().rooms),

    totalRooms: () => get().rooms.length,

    isQuartetGroup: () => {
      const totalPax = get().roomsUtils.totalPax();
      return totalPax > 0 && totalPax % 4 === 0;
    },

    generateSummaryText: () => {
      const { roomsUtils } = get();
      const totalPax = roomsUtils.totalPax();
      const totalRooms = roomsUtils.totalRooms();

      return totalRooms > 0
        ? `${pluralize("Golfer", totalPax, true)}, ${pluralize("Room", totalRooms, true)}`
        : "";
    },
  },
});
